import { useCallback, useEffect, useState, useTransition, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { ContentContainer } from '../../../components/Filters/components/ContentContainer';
import { ControlsPanel } from '../../../components/Filters/components/ControlsPanel';
import { CheckboxList } from '../../../components/Filters/components/CheckboxList';
import { SearchInput } from '../../../components/Filters/components/SearchInput';
import { ItemBadgesContainer } from '../../../components/Filters/components/ItemBadgesContainer';
import { FilterLoader } from '../../../components/Filters/components/FilterLoader';
import { NoResults } from '../../../components/Filters/components/NoResults';
import { API } from '../../../API';
import { showRequestError } from '../../../utils/requestError';
import { useTranslation } from 'react-i18next';
import { useSearchContext } from '../contexts';
import { filterLanguages } from '../../../utils/languages';

export interface LanguageOption {
  value: string;
  label: string;
  id: number;
}

export interface LanguagesResponse {
  objects: LanguageData[];
  total_count: number;
}

export interface LanguageData {
  id: number;
  name: string;
  code: string;
}

export const LanguageChooser = () => {
  const { t } = useTranslation();
  const { state, updateDraftFilters } = useSearchContext();
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [defaultOptions, setDefaultOptions] = useState<LanguageOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<LanguageOption[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isPending, startTransition] = useTransition();

  let userLanguage = 'en';

  if(
    localStorage.getItem('i18nextLng') === 'ua-UK' ||
    localStorage.getItem('i18nextLng') === 'ru-RU' ||
    localStorage.getItem('i18nextLng') === 'ru' ||
    localStorage.getItem('i18nextLng') === 'uk'
  ) {
   userLanguage = 'uk';
  } else {
    userLanguage = 'en';
  }

  // Use draft filters from context
  const selectedLanguages = state.draftFilters.languages.value;

  const getAllLanguages = useCallback(() => {
    startTransition(() => {
      API.fetch('GET', `/API/v1/languages`).then((data: LanguagesResponse) => {
        let optionsList = data.objects
          .slice(0, 10)
          .map((language: LanguageData) => {
            return { value: language.code, label: t(language.name), id: language.id };
          });
        setDefaultOptions(optionsList);
      }).catch((error) => showRequestError(error, t('Languages')));
    });
  }, [t]);

  useEffect(getAllLanguages, [getAllLanguages]);

  // Fetch selected languages when they change
  useEffect(() => {
    if (state.draftFilters.languages.value.length === 0) {
      setSelectedOptions([]);
      return;
    }

    const urlParams = new URLSearchParams();
    state.draftFilters.languages.value.forEach(id => {
      urlParams.append('ids', id.toString());
    });

    API.fetch('GET', `/API/v1/search-languages?${urlParams.toString()}`).then((data: LanguagesResponse) => {
      let options = data.objects
        .sort((a, b) => (a.name > b.name ? 1 : -1))
        .map((language: LanguageData) => {
          return { value: language.code, label: t(language.name), id: language.id };
        });
      setSelectedOptions(options);
    }).catch((error) => showRequestError(error, t('Languages')));
  }, [state.draftFilters.languages.value, t]);

  const getLanguages = (inputValue: string) => {
    if (!inputValue) {
      setLanguages([]);
      return;
    }

    startTransition(() => {
      const filteredLanguages = filterLanguages(inputValue, userLanguage);
      const sortedLanguages = filteredLanguages.sort((a: LanguageOption, b: LanguageOption) => (a.label > b.label ? 1 : -1));
      setLanguages(sortedLanguages);
    });
  };

  const debouncedGetLanguages = useCallback(
    debounce((value: string) => {
      getLanguages(value);
    }, 300),
    []
  );

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    debouncedGetLanguages(value);
  };

  const handleClearAll = () => {
    updateDraftFilters({
      languages: {
        value: [],
        exclude: false,
      },
    });
  };

  const handleToggleLanguage = useCallback((languageValue: number) => {
    if (selectedLanguages.includes(languageValue)) {
      updateDraftFilters({
        languages: {
          value: selectedLanguages.filter((lang) => lang !== languageValue),
          exclude: state.draftFilters.languages.exclude,
        },
      });
    } else {
      updateDraftFilters({
        languages: {
          value: [...selectedLanguages, languageValue],
          exclude: state.draftFilters.languages.exclude,
        },
      });
    }
  }, [selectedLanguages, state.draftFilters.languages.exclude, updateDraftFilters]);

  const listToShow = languages.length > 0 ? languages : defaultOptions;
  const isNoResults = !isPending && searchValue && languages.length === 0;

  // Memoize the checkbox items to prevent recreating on every render
  const checkboxItems = useMemo(() => {
    return listToShow.map((language: LanguageOption) => ({
      name: language.label,
      checked: selectedLanguages.includes(language.id),
      onChange: () => handleToggleLanguage(language.id),
    }));
  }, [listToShow, selectedLanguages, handleToggleLanguage]);

  // Memoize badges to prevent recreating on every render
  const badges = useMemo(() => {
    return selectedOptions.map(language => ({
      name: language.label,
      onChange: () => handleToggleLanguage(language.id)
    }));
  }, [selectedOptions, handleToggleLanguage]);

  return (
    <ContentContainer
      searchComponent={
        <SearchInput
          placeholder={t('Search languages')}
          value={searchValue}
          onChange={handleSearchChange}
        />
      }
      actionsComponent={
        <ControlsPanel
          id="languages-filter"
          count={selectedLanguages.length}
          isExclude={state.draftFilters.languages.exclude}
          showExcludeSwitch={true}
          showSelectAll={false}
          onClearAll={handleClearAll}
          onSelectAll={() => {}}
          onExcludeClick={() => {
            updateDraftFilters({
              languages: {
                value: state.draftFilters.languages.value,
                exclude: !state.draftFilters.languages.exclude,
              },
            });
          }}
        />
      }
      badgesComponent={
        selectedOptions.length > 0 ? (
          <ItemBadgesContainer
            badges={badges}
          />
        ) : undefined
      }
    >
      {isPending ? <FilterLoader /> : isNoResults ? <NoResults /> : (
        <CheckboxList
          items={checkboxItems}
        />
      )}
    </ContentContainer>
  );
};
